import {
  IError,
  LoadingOverlay,
  useToast,
  Button,
  ButtonVariants,
} from '@comptia-sso/core';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import { ReactElement, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

// Components.
import { InstitutionDetails } from '../../components/InstitutionDetails';
import { InstitutionEditHeader } from './InstitutionEditHeader';

// Hooks.
import {
  useInstitution,
  useInstructorApproversByInstitution,
  useAccount,
} from 'hooks';

// Layouts.
import { ContentLayout } from 'layouts';

// Styles.
import styles from './InstitutionEditView.module.scss';

export const InstitutionEditView = (): ReactElement => {
  const { id } = useParams();
  const [account] = useAccount();
  const [popToast] = useToast();
  const [institution, { error, isLoading }] = useInstitution(id);
  const [instructors, { isLoading: instructorsLoading }] =
    useInstructorApproversByInstitution(id);

  useEffect(() => {
    if (error) {
      popToast(
        (error as IError)?.message ||
          'An unexpected error occurred. Please try again later.',
      );
    }
  }, [error]);

  return (
    <>
      <ContentLayout
        header={<InstitutionEditHeader />}
        content={
          !isLoading &&
          !error && (
            <>
              <InstitutionDetails
                institution={institution}
                account={account}
                mode="edit"
              />
              <div className={styles.instructorsSection}>
                <div className={styles.sectionHeader}>
                  <h3>Instructor Approvers ({instructors?.length || 0})</h3>
                  {!!account?.permissions?.editInstructorApprovers && (
                    <Button
                      className={styles.add}
                      link
                      to={`/consent-approvers/instructors/add?institutionId=${id}`}
                      variant={ButtonVariants.Primary}
                    >
                      Add Instructor
                    </Button>
                  )}
                </div>
                {instructors?.length > 0 ? (
                  instructors.map((instructor) => (
                    <Link
                      key={instructor.id}
                      className={styles.instructorItem}
                      to={`/consent-approvers/instructors/${instructor.id}`}
                    >
                      <div className={styles.item}>
                        <strong>Name</strong>
                        <span>
                          {instructor.firstName} {instructor.lastName}
                        </span>
                      </div>
                      <div className={styles.item}>
                        <strong>Email</strong>
                        <span>{instructor.email}</span>
                      </div>
                      <div className={styles.item}>
                        <strong>Status</strong>
                        <span>
                          {instructor.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className={styles.link}>
                        <ArrowForwardIos />
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className={styles.empty}>
                    No instructor approvers for this institution.
                  </div>
                )}
              </div>
            </>
          )
        }
      />
      <LoadingOverlay
        isOpen={isLoading || instructorsLoading}
        text="Please Wait"
      />
    </>
  );
};
